import { create } from "zustand";

/** Which softphone surface a leg belongs to. */
export type ActiveCallSource = "inbound" | "outbound";

interface ActiveCallState {
  /**
   * Call legs currently occupying this user's audio, keyed by leg id.
   *
   * The inbound provider and the outbound dialer each run their own
   * SignalWire client, so neither can see the other's calls. This store is
   * the one place that knows whether the user is on a call at all, which is
   * what lets the inbound provider reject a second call that arrives while
   * an outbound call is up.
   */
  activeLegs: Record<string, ActiveCallSource>;
  beginCall: (legId: string, source: ActiveCallSource) => void;
  endCall: (legId: string) => void;
}

export const useActiveCallStore = create<ActiveCallState>((set) => ({
  activeLegs: {},
  beginCall: (legId, source) =>
    set((state) =>
      state.activeLegs[legId] === source
        ? state
        : { activeLegs: { ...state.activeLegs, [legId]: source } },
    ),
  endCall: (legId) =>
    set((state) => {
      if (!(legId in state.activeLegs)) return state;
      const activeLegs = { ...state.activeLegs };
      delete activeLegs[legId];
      return { activeLegs };
    }),
}));

/**
 * Whether the user is already on a call, on any softphone surface.
 *
 * A leg counts from the moment it starts ringing, not from when it is
 * answered: someone listening to an outbound call ring is just as
 * unavailable as someone mid-conversation, and that window is exactly when
 * a second inbound ring is most disruptive.
 *
 * Reads the store imperatively because the callers are SignalWire SDK
 * callbacks that run outside React's render cycle — a subscribed value
 * would be captured in a stale closure.
 */
export function isOnCall(): boolean {
  return Object.keys(useActiveCallStore.getState().activeLegs).length > 0;
}
